import { useEffect, useState } from 'react'
import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  styled,
  Typography,
} from '@mui/material'
import { intl } from '../../../../i18n'
import { AggregateField } from '../../../../domain/entity/WbsItemEntity'
import { WorkloadUnit } from '../../../../lib/functions/workload'
import { AggregateFieldToggleGroup } from '../../../components/toggleGroups/AggregateFieldToggleGroup'
import { WorkloadUnitToggleGroup } from '../../../components/toggleGroups/WorkloadUnitSelect'
import { Button } from '../../../components/buttons'
import { RadioButton } from '../../../components/radio-button'
import { colorPalette } from '../../../style/colorPalette'
import {
  PROGRAM_PROJECT_REPORT_INPUT_MODE,
  ProgramProjectReportInputMode,
} from '../model/reportRow'

const INPUT_MODE_OPTIONS = [
  {
    value: PROGRAM_PROJECT_REPORT_INPUT_MODE.AGGREGATION,
    labelId: 'programProjectReport.inputMode.aggregation',
    descriptionId: 'programProjectReport.inputMode.aggregationDescription',
  },
  {
    value: PROGRAM_PROJECT_REPORT_INPUT_MODE.FREE,
    labelId: 'programProjectReport.inputMode.free',
    descriptionId: 'programProjectReport.inputMode.freeDescription',
  },
] as const

type Mode = 'initial' | 'change'

type Props = {
  mode: Mode
  isOpen: boolean
  defaultField: AggregateField
  defaultWorkloadUnit: WorkloadUnit
  currentInputMode?: ProgramProjectReportInputMode
  onSubmit: (
    field: AggregateField,
    unit: WorkloadUnit,
    deleteExistingData: boolean,
    inputMode?: ProgramProjectReportInputMode
  ) => void
  onClose?: () => void
}

export const UnitSettingDialog = (props: Props) => {
  const {
    mode,
    isOpen,
    defaultField,
    defaultWorkloadUnit,
    currentInputMode,
    onSubmit,
    onClose,
  } = props

  const [field, setField] = useState<AggregateField>(defaultField)
  const [unit, setUnit] = useState<WorkloadUnit>(defaultWorkloadUnit)
  const [deleteConfirmed, setDeleteConfirmed] = useState<boolean>(false)
  const [selectedInputMode, setSelectedInputMode] =
    useState<ProgramProjectReportInputMode>(
      currentInputMode ?? PROGRAM_PROJECT_REPORT_INPUT_MODE.AGGREGATION
    )

  useEffect(() => {
    if (isOpen) {
      setField(defaultField)
      setUnit(defaultWorkloadUnit)
      setDeleteConfirmed(false)
      setSelectedInputMode(
        currentInputMode ?? PROGRAM_PROJECT_REPORT_INPUT_MODE.AGGREGATION
      )
    }
  }, [isOpen, defaultField, defaultWorkloadUnit, currentInputMode])

  const fieldChanged = field !== defaultField
  const inputModeChanged = selectedInputMode !== currentInputMode
  const hasChanges = fieldChanged || inputModeChanged

  return (
    <Dialog
      open={isOpen}
      fullWidth={true}
      maxWidth="xs"
      PaperProps={{ sx: { width: 460, maxWidth: '90vw' } }}
      disableEscapeKeyDown={mode === 'initial'}
      onClose={() => {
        if (mode === 'initial') return
        onClose?.()
      }}
      aria-labelledby="unit-setting-dialog-title"
    >
      <DialogTitle id="unit-setting-dialog-title" sx={{ fontSize: 16 }}>
        {intl.formatMessage({
          id:
            mode === 'initial'
              ? 'programProjectReport.unitSetting.initialTitle'
              : 'programProjectReport.unitSetting.changeTitle',
        })}
      </DialogTitle>
      <DialogContent dividers={true} sx={{ px: 3, py: 2.5 }}>
        {mode === 'initial' && (
          <Typography sx={{ mb: 2.5, color: 'text.secondary', fontSize: 13 }}>
            {intl.formatMessage({
              id: 'programProjectReport.unitSetting.description',
            })}
            <br />
            {intl.formatMessage({
              id: 'programProjectReport.unitSetting.descriptionUsage',
            })}
          </Typography>
        )}

        <Box sx={{ mb: 2.5 }}>
          <Typography sx={{ mb: 0.5, fontWeight: 600, fontSize: 14 }}>
            {intl.formatMessage({
              id: 'programProjectReport.inputMode.label',
            })}
          </Typography>
          <InputModeRadioGroup>
            {INPUT_MODE_OPTIONS.map(opt => (
              <RadioOption
                key={opt.value}
                onClick={() => setSelectedInputMode(opt.value)}
              >
                <RadioButton
                  name="inputMode"
                  checked={selectedInputMode === opt.value}
                  onChange={() => setSelectedInputMode(opt.value)}
                />
                <RadioTextWrapper>
                  <Typography sx={{ fontSize: 14 }}>
                    {intl.formatMessage({ id: opt.labelId })}
                  </Typography>
                  <Typography sx={{ color: 'text.secondary', fontSize: 12 }}>
                    {intl.formatMessage({ id: opt.descriptionId })}
                  </Typography>
                </RadioTextWrapper>
              </RadioOption>
            ))}
          </InputModeRadioGroup>
          {selectedInputMode ===
            PROGRAM_PROJECT_REPORT_INPUT_MODE.AGGREGATION &&
            currentInputMode === PROGRAM_PROJECT_REPORT_INPUT_MODE.FREE && (
              <Typography
                sx={{ mt: 1, color: colorPalette.pink[5], fontSize: 12 }}
              >
                {intl.formatMessage({
                  id: 'programProjectReport.inputMode.changeWarning',
                })}
              </Typography>
            )}
        </Box>

        <Box sx={{ mb: 2.5 }}>
          <Typography sx={{ mb: 0.5, fontWeight: 600, fontSize: 14 }}>
            {intl.formatMessage({
              id: 'programProjectReport.unitSetting.aggregateFieldLabel',
            })}
          </Typography>
          <Typography sx={{ mb: 1, color: 'text.secondary', fontSize: 12 }}>
            {intl.formatMessage({
              id: 'programProjectReport.unitSetting.aggregateFieldCaption',
            })}
          </Typography>
          <AggregateFieldToggleGroup
            value={field}
            onChange={value => setField(value)}
          />
        </Box>

        {mode === 'initial' && (
          <Box sx={{ mb: 2.5 }}>
            <Typography sx={{ mb: 0.5, fontWeight: 600, fontSize: 14 }}>
              {intl.formatMessage({
                id: 'programProjectReport.unitSetting.workloadUnitLabel',
              })}
            </Typography>
            <Typography sx={{ mb: 1, color: 'text.secondary', fontSize: 12 }}>
              {intl.formatMessage({
                id: 'programProjectReport.unitSetting.workloadUnitCaption',
              })}
            </Typography>
            <WorkloadUnitToggleGroup value={unit} refresh={setUnit} />
          </Box>
        )}

        {mode === 'change' && fieldChanged && (
          <FormControlLabel
            sx={{ mt: 1, alignItems: 'flex-start' }}
            control={
              <Checkbox
                checked={deleteConfirmed}
                onChange={e => setDeleteConfirmed(e.target.checked)}
              />
            }
            label={
              <Typography sx={{ mt: 0.75 }}>
                {intl.formatMessage({
                  id: 'programProjectReport.unitSetting.deleteProgress',
                })}
              </Typography>
            }
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button
          colorPattern="skyBlue"
          disabled={mode === 'change' && !hasChanges}
          onClick={() =>
            onSubmit(field, unit, deleteConfirmed, selectedInputMode)
          }
        >
          {intl.formatMessage({
            id:
              mode === 'initial'
                ? 'programProjectReport.unitSetting.submit'
                : 'programProjectReport.unitSetting.change',
          })}
        </Button>
        {mode === 'change' && (
          <Button colorPattern="monotone" variant="outlined" onClick={onClose}>
            {intl.formatMessage({ id: 'dialog.cancel' })}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

const InputModeRadioGroup = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  paddingLeft: 4,
})

const RadioOption = styled('div')({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 8,
  cursor: 'pointer',
})

const RadioTextWrapper = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
})
